import axios from "axios"

class Search {
	constructor() {
		this.rootUrl = window.gentleAngelsData?.rootUrl || window.universityData?.root_url || window.location.origin
		this.endpoint = `${this.rootUrl}/wp-json/gentle-angels/v1/search`

		this.typingTimer = null
		this.previousValue = ""
		this.isOverlayOpen = false
		this.isLoading = false
		this.abortController = null

		this.addSearchHTML()
		this.cacheDom()
		this.bindEvents()
	}

	cacheDom() {
		this.overlay = document.querySelector(".search-overlay")
		this.results = document.querySelector("#search-overlay__results")
		this.openButtons = document.querySelectorAll(".js-search-trigger")
		this.closeButton = document.querySelector(".search-overlay__close")
		this.searchField = document.querySelector("#search-term")
	}

	bindEvents() {
		this.openButtons.forEach((button) => {
			button.addEventListener("click", (event) => {
				event.preventDefault()
				this.openOverlay()
			})
		})

		this.closeButton?.addEventListener("click", () => this.closeOverlay())
		this.searchField?.addEventListener("input", () => this.typingLogic())
		document.addEventListener("keydown", (event) => this.keyPressDispatcher(event))
	}

	typingLogic() {
		const searchValue = this.searchField.value.trim()

		if (searchValue === this.previousValue) {
			return
		}

		clearTimeout(this.typingTimer)

		if (!searchValue) {
			this.results.innerHTML = ""
			this.isLoading = false
			this.previousValue = searchValue
			this.abortController?.abort()
			return
		}

		if (!this.isLoading) {
			this.results.innerHTML = '<div class="spinner-loader" aria-label="Loading search results"></div>'
			this.isLoading = true
		}

		this.typingTimer = setTimeout(() => this.getResults(searchValue), 500)
		this.previousValue = searchValue
	}

	async getResults(searchValue) {
		try {
			this.abortController?.abort()
			this.abortController = new AbortController()

			const response = await axios.get(this.endpoint, {
				params: { term: searchValue },
				signal: this.abortController.signal
			})

			this.renderResults(response.data)
		} catch (error) {
			if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
				return
			}

			this.results.innerHTML = `
				<p class="search-overlay__message">
					Search is temporarily unavailable. Please try again.
				</p>
			`
		} finally {
			this.isLoading = false
		}
	}

	renderResults(results = {}) {
		const pages = results.pages || results.generalInfo || []
		const blogs = results.blogs || results.posts || []
		const events = results.events || []
		const testimonials = results.testimonials || []
		const categories = results.categories || []
		const tags = results.tags || []

		this.results.innerHTML = `
			<div class="search-overlay__grid">
				${this.renderColumn("Pages", pages, "No pages match that search.")}
				${this.renderColumn("Blogs", blogs, "No blog posts match that search.")}
				${this.renderColumn("Events", events, "No events match that search.")}
				${this.renderColumn("Testimonials", testimonials, "No testimonials match that search.")}
				${this.renderColumn("Categories", categories, "No categories match that search.")}
				${this.renderColumn("Tags", tags, "No tags match that search.")}
			</div>
		`
	}

	renderColumn(title, items = [], emptyMessage) {
		return `
			<section class="search-overlay__section">
				<h2 class="search-overlay__section-title">${this.escapeHTML(title)}</h2>

				${
					items.length
						? `<ul class="search-overlay__list">
							${items.map((item) => this.renderResultItem(item)).join("")}
						</ul>`
						: `<p>${this.escapeHTML(emptyMessage)}</p>`
				}
			</section>
		`
	}

	renderResultItem(item = {}) {
		const title = item.title || item.name || "Untitled"
		const permalink = item.permalink || item.link || "#"
		const excerpt = item.excerpt || item.description || ""
		const count = Number.isInteger(item.count) ? item.count : null

		return `
			<li class="search-overlay__item">
				<a href="${this.escapeAttribute(permalink)}">${this.escapeHTML(title)}</a>
				${excerpt ? `<p>${this.escapeHTML(excerpt)}</p>` : ""}
				${count !== null ? `<span class="search-overlay__count">${count} item${count === 1 ? "" : "s"}</span>` : ""}
			</li>
		`
	}

	keyPressDispatcher(event) {
		const activeTag = document.activeElement?.tagName
		const isTyping = activeTag === "INPUT" || activeTag === "TEXTAREA"

		if (event.key.toLowerCase() === "s" && !this.isOverlayOpen && !isTyping) {
			this.openOverlay()
		}

		if (event.key === "Escape" && this.isOverlayOpen) {
			this.closeOverlay()
		}
	}

	openOverlay() {
		this.overlay.classList.add("search-overlay--active")
		document.body.classList.add("body-no-scroll")
		this.searchField.value = ""
		this.results.innerHTML = ""
		this.previousValue = ""
		this.isOverlayOpen = true

		setTimeout(() => this.searchField.focus(), 250)
	}

	closeOverlay() {
		this.overlay.classList.remove("search-overlay--active")
		document.body.classList.remove("body-no-scroll")
		this.abortController?.abort()
		this.isOverlayOpen = false
		this.isLoading = false
	}

	addSearchHTML() {
		document.body.insertAdjacentHTML(
			"beforeend",
			`
			<div class="search-overlay" role="dialog" aria-modal="true" aria-label="Site search">
				<div class="search-overlay__top">
					<div class="container">
						<i class="fa fa-search search-overlay__icon" aria-hidden="true"></i>

						<label class="screen-reader-text" for="search-term">Search</label>
						<input
							type="search"
							class="search-term"
							placeholder="What are you looking for?"
							id="search-term"
							autocomplete="off"
						>

						<button class="search-overlay__close" type="button" aria-label="Close search">
							<i class="fa fa-times" aria-hidden="true"></i>
						</button>
					</div>
				</div>

				<div class="container">
					<div id="search-overlay__results" aria-live="polite"></div>
				</div>
			</div>
			`
		)
	}

	escapeHTML(value) {
		return String(value)
			.replaceAll("&", "&amp;")
			.replaceAll("<", "&lt;")
			.replaceAll(">", "&gt;")
			.replaceAll('"', "&quot;")
			.replaceAll("'", "&#039;")
	}

	escapeAttribute(value) {
		return this.escapeHTML(value)
	}
}

export default Search